import {
  Button,
  DialogHeader,
  DialogTitle,
  showDialog,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useIsMobile,
  Card,
} from '@bpinternal/ui-kit-next'
import { Link } from '@tanstack/react-router'
import { Check, ExternalLink, Files, Info } from 'lucide-react'
import { useRef, useState } from 'react'

type ApiDocumentationDialogProps = {
  workspaceId: string
}

export const showApiDocumentationDialog = ({ workspaceId }: ApiDocumentationDialogProps) => {
  return showDialog(() => <ApiDocumentationDialog workspaceId={workspaceId} />, { size: 'max-w-6xl' })
}

export const ApiDocumentationDialog = ({ workspaceId }: ApiDocumentationDialogProps) => {
  const isMobile = useIsMobile()

  return (
    <div className="flex h-[70vh] max-h-[70vh] flex-col gap-3 md:h-[600px]">
      <DialogHeader>
        <DialogTitle>API Documentation</DialogTitle>
      </DialogHeader>

      <div className="flex min-h-0 w-full flex-1 flex-col gap-6 overflow-y-auto md:flex-row md:overflow-y-visible">
        <Tabs
          defaultValue="overview"
          orientation={isMobile ? 'horizontal' : 'vertical'}
          className="flex flex-col gap-6 md:flex-row"
        >
          <TabsList className="flex h-fit flex-row md:flex-col">
            <TabsTrigger value="overview" className="w-full justify-start">
              Overview
            </TabsTrigger>
            <TabsTrigger value="botpress-client" className="w-full justify-start">
              Using the Botpress Client
            </TabsTrigger>
          </TabsList>

          <div className="-mr-6 min-h-0 flex-1 overflow-y-auto pr-6">
            <TabsContent value="overview" className="h-full">
              <Overview />
            </TabsContent>

            <TabsContent value="botpress-client" className="h-full">
              <BotpressClient workspaceId={workspaceId} />
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  )
}

const Overview = () => {
  return (
    <div className="space-y-6 text-foreground">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Overview</h2>
        <Button variant="outline" size="sm" asChild>
          <a href="https://botpress.com/docs" target="_blank" rel="noopener noreferrer">
            <ExternalLink className="mr-2 h-4 w-4" />
            File API Docs
          </a>
        </Button>
      </div>

      <div className="space-y-4">
        <p className="text-sm">
          The Knowledge Bases use the Botpress File API to manage files. The File API allows you to upload, list, search
          and delete files programmatically.
        </p>

        <Card className="flex flex-row items-center gap-2 border-primary p-4 text-primary">
          <Info size={24} />
          <div>
            The two most important things to remember when using the File API for the Knowledge Base are:
            <ul className="ml-4 mt-2 list-inside list-disc space-y-1">
              <li>
                Files must be tagged with the <InlineCode>source</InlineCode> and <InlineCode>kbId</InlineCode> tags to
                be associated with the knowledge base.
              </li>
              <li>
                Files must be indexed (<InlineCode>index: true</InlineCode>) to be searchable by the bot.
              </li>
            </ul>
          </div>
        </Card>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Uploading a File</h3>
        <p className="text-sm">
          To upload a file to the knowledge base, call the <InlineCode>uploadFile</InlineCode> method and pass the file
          as a parameter. If you upload a file with the same <InlineCode>key</InlineCode>, the file will be overwritten.
        </p>

        <CodeBlock>
          {`const { file } = await client.uploadFile({
  key: 'kb_01K6B7PQ8ABX2WC2MW3H5XK5Y1/my-file.txt',
  accessPolicies: [],
  content: 'The content of your file here',
  index: true,
  tags: {
    source: 'knowledge-base',
    kbId: 'kb_01K6B7PQ8ABX2WC2MW3H5XK5Y1',
    title: 'My File'
  }
})`}
        </CodeBlock>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Letting users upload files</h3>
        <p className="text-sm">
          Alternatively, you can separate the creation of a file and the uploading of the content by using the{' '}
          <InlineCode>upsertFile</InlineCode> method. This is useful when you want to delegate the file upload to a
          different service or chat users. To do this, set the <InlineCode>content</InlineCode> property to{' '}
          <InlineCode>undefined</InlineCode> and the <InlineCode>size</InlineCode> property to the size of the file in
          bytes. The <InlineCode>upsertFile</InlineCode> method will return a pre-signed URL that you can use to upload
          the file.
        </p>

        <CodeBlock>
          {`// On the backend, create a pre-signed URL for the file upload
// This must be done on the backend because it requires authentication
// The backend needs to know the file size and file name in advance

const content = 'Hello, world!'
const fileTitle = 'myFile.txt'
const kbId = 'my-kb-id'
const fileSize = 1024 // in bytes

const { file } = await client.upsertFile({
  key: \`\${kbId}/\${fileTitle}\`,
  size: fileSize,
  index: true,
  tags: {
    source: 'knowledge-base',
    kbId,
    title: fileTitle
  }
})

// -------------------
// On the frontend, upload the file using the pre-signed URL.
// This can be done anywhere and doesn't require authentication.

await axios.put(file.uploadUrl, content, {
  maxBodyLength: Infinity
})`}
        </CodeBlock>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Syncing Files from Integrations</h3>
        <p className="text-sm">
          Integrations can upload files to the Knowledge Base by using the File API. To do this, the integration must
          provide the <InlineCode>kbId</InlineCode> and <InlineCode>source</InlineCode> tags in the file metadata.
        </p>
      </div>

      <div className="border-t pt-4">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Listing Knowledge Base files</h3>
          <p className="text-sm">
            To list all files inside the Knowledge Base, use the <InlineCode>listFiles</InlineCode> method.
          </p>

          <CodeBlock>
            {`const { files } = await client.listFiles({
  tags: {
    source: 'knowledge-base',
    kbId: 'kb_01K6B7PQ8ABX2WC2MW3H5XK5Y1'
  }
})`}
          </CodeBlock>
        </div>
      </div>
    </div>
  )
}

const BotpressClient = ({ workspaceId }: { workspaceId: string }) => {
  return (
    <div className="space-y-6 text-foreground">
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Using the Botpress Client</h2>
        <p className="text-sm">
          The Botpress Client is accessible in the code editor and can be utilized via the{' '}
          <InlineCode>client</InlineCode> object. Remember to await the results from operations.
        </p>
      </div>

      <CodeBlock>
        {`const { tables } = await client.listTables({})
const { files } = await client.listFiles({})`}
      </CodeBlock>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Installation</h3>
        <p className="text-sm">
          To install the client in another project, execute <InlineCode>npm install @botpress/client</InlineCode>. A{' '}
          <Link
            to="/workspaces/$workspaceId/profile/settings"
            params={{ workspaceId }}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Personal Access Token
          </Link>{' '}
          is required for client authentication, obtainable from Botpress Cloud.
        </p>

        <CodeBlock>
          {`import { Client } from '@botpress/client'

const client = new Client({
  token: 'YOUR_PAT_HERE',
  botId: '96654f5b-2ad0-4b48-849b-0f8034180caf',
  workspaceId: 'wkspace_01JEE3STB6PDP9BVNPSHBYZDZQ'
})`}
        </CodeBlock>
      </div>
    </div>
  )
}

const InlineCode = ({ children }: { children: React.ReactNode }) => {
  return <code className="rounded-sm bg-primary/10 px-0.5">{children}</code>
}

const CodeBlock = ({ children, language = 'javascript' }: { children: React.ReactNode; language?: string }) => {
  const [codeCopied, setCodeCopied] = useState(false)
  const codeRef = useRef<HTMLPreElement>(null)

  const handleCodeCopy = () => {
    if (!codeRef.current) return
    navigator.clipboard.writeText(codeRef.current.textContent || '').then(() => {
      setCodeCopied(true)
      setTimeout(() => setCodeCopied(false), 1500)
    })
  }

  return (
    <div className="rounded-md border border-border bg-background">
      <div className="flex items-center justify-between rounded-t-md border-b border-border bg-muted px-3 py-2 text-xs text-foreground">
        <span>{language}</span>
        {codeCopied ? (
          <Check className="h-4 w-4" />
        ) : (
          <Files className="h-4 w-4 cursor-pointer" onClick={handleCodeCopy} />
        )}
      </div>
      <pre ref={codeRef} className="overflow-x-auto p-3 text-xs">
        <code>{children}</code>
      </pre>
    </div>
  )
}
